import classNames from 'classnames';
import React, { useRef, useState } from 'react';
import { useGetCustomer } from '../../../hooks/apolloHooks/useGetCustomer';
import { useUpdateAutogiro } from '../../../hooks/apolloHooks/useUpdateAutogiro';
import { GET_AUTOGIRO_DATA_QUERY } from '../../../integrations/allkort/graphql/gql/CustomerQueries';
import { AutogiroDataResponse } from '../../../integrations/allkort/graphql/interface/Customer';
import { ConnectPlugIcon, InfoIconOutlined } from '../../svgs/ExpressonSVGs';
import Button from '../Button';
import FetchingAnimation from '../FetchingAnimation';
import RightsWrapper from '../RightsWrapper';
import Tooltip from '../Tooltip';

type AutogiroSettingsProps = {
    customerId: number | null;
    wrapperClasses?: string;
};

const AutogiroSettings: React.FC<AutogiroSettingsProps> = ({ customerId, wrapperClasses = '' }) => {
    const [clearingNumber, setClearingNumber] = useState('');
    const [clearingNumberError, setClearingNumberError] = useState<string | undefined>(undefined);
    const [bankNumber, setBankNumber] = useState('');
    const [bankNumberError, setBankNumberError] = useState<string | undefined>(undefined);

    const dialogRefAutogiro = useRef<HTMLDialogElement>(null);

    const { data: autogiroData, loading: autogiroLoading, refetch: refetchAutogiro } = useGetCustomer<AutogiroDataResponse>(
        GET_AUTOGIRO_DATA_QUERY,
        undefined,
        { fetchPolicy: 'network-only', skip: !customerId }
    );
    const autogiroApproved = autogiroData?.currentCustomer?.autogiro.approved ?? false;
    const autogiroChangeRequested = autogiroData?.currentCustomer?.autogiro.changeRequested ?? false;
    const autogiroChangeRequestDate = autogiroData?.currentCustomer?.autogiro.changeRequestedAt ? new Date(autogiroData.currentCustomer.autogiro.changeRequestedAt) : null;

    const changeRequestedDaysAgo = autogiroChangeRequestDate
        ? (Date.now() - autogiroChangeRequestDate.getTime()) / (1000 * 60 * 60 * 24)
        : null;
    // A change is in progress when:
    // - not approved yet but changeRequested is true (pending initial/re-application), OR
    // - approved but a recent change was made less than 6 days ago (processing update)
    const recentChangeInProgress =
        (!autogiroApproved && autogiroChangeRequested) ||
        (autogiroApproved && changeRequestedDaysAgo !== null && changeRequestedDaysAgo < 6);
    const showAutogiroButton = !recentChangeInProgress;
    const { updateAutogiro, loading: mutationLoading } = useUpdateAutogiro();
    const openAutogiroDialog = () => {
        dialogRefAutogiro.current?.showModal();
    };

    const handleCancelAutogiro = () => {
        dialogRefAutogiro.current?.close();
        setClearingNumber('');
        setClearingNumberError(undefined);
        setBankNumber('');
        setBankNumberError(undefined);
    };

    const handleClearingNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (value && !/^\d*$/.test(value)) return;
        if (value.length > 4) return;
        setClearingNumber(value);
        if (value.length === 0) {
            setClearingNumberError(undefined);
            return;
        }
        if (value.length < 4) {
            setClearingNumberError('Clearingnummer måste vara 4 siffror');
        } else {
            setClearingNumberError(undefined);
        }
    };

    const handleClearingNumberBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (value.length === 0) return;
        if (value.length < 4) {
            setClearingNumberError('Clearingnummer måste vara 4 siffror');
        } else {
            setClearingNumberError(undefined);
        }
    };

    const handleBankNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (value && !/^\d*$/.test(value)) return;
        setBankNumber(value);
        if (value.length === 0) {
            setBankNumberError(undefined);
        }
    };

    const handleBankNumberBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (value.length === 0) return;
        if (value.length < 1) {
            setBankNumberError('Kontonummer krävs');
        } else {
            setBankNumberError(undefined);
        }
    };

    const isFormValid = (): boolean => {
        if (clearingNumber.length !== 4) return false;
        if (bankNumber.length < 1) return false;
        if (clearingNumberError || bankNumberError) return false;
        return true;
    };

    const handleSubmitAutogiro = async () => {
        if (!customerId || !isFormValid()) return;
        await updateAutogiro({
            customerId,
            requestedClearingNumber: clearingNumber,
            requestedAccountNumber: bankNumber,
            approved: true,
        });
        dialogRefAutogiro.current?.close();
        setClearingNumber('');
        setBankNumber('');
        await refetchAutogiro();
    };

    const handleDeleteAutogiro = async () => {
        if (!customerId) return;
        await updateAutogiro({
            customerId,
            requestedClearingNumber: '',
            requestedAccountNumber: '',
            approved: false,
        });
        dialogRefAutogiro.current?.close();
        await refetchAutogiro();
    };

    return (
        <div className={classNames('autogiro-wrapper', wrapperClasses)}>
            <div className="autogiro-header-content">
                <h5 className="body-text">
                    Betala med autogiro
                    {autogiroLoading ? (
                        <FetchingAnimation
                            width={75}
                            height={21}
                            borderRadius={9999}
                        />
                    ) : (
                        showAutogiroButton ? (
                            autogiroApproved ? (
                                <span className="active">Aktiv</span>
                            ) : (
                                <span className="inactive">Inaktiv</span>
                            )
                        ) : null
                    )}
                </h5>
                <div className="inner">
                    {autogiroApproved && !autogiroLoading ? (
                        <div>
                            {showAutogiroButton ? (
                                <p>
                                    Du har aktiverat autogiro. Dina fakturor kommer att betalas automatiskt på förfallodagen via autogiro.
                                    Du kan även ändra eller inaktivera autogiro.
                                </p>
                            ) : (
                                <p>
                                    Du har en pågående ansökan/ändring för autogiro som mottagits {autogiroChangeRequestDate ? `den ${autogiroChangeRequestDate.toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' })}` : ''} och väntar på godkännande.
                                </p>
                            )}

                        </div>
                    ) : (
                        <div>
                            {autogiroChangeRequested ? (
                                <p>
                                    Din ansökan/ändring för autogiro har mottagits {autogiroChangeRequestDate ? `den ${autogiroChangeRequestDate.toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' })}` : ''} och väntar på godkännande.
                                </p>
                            ) : (
                                <p>
                                    Betala smidigt med autogiro, aktivera genom att klicka på knappen nedan och fylla i dina bankuppgifter. När du har anslutit autogiro kommer dina fakturor att betalas automatiskt varje månad på förfallodagen.
                                </p>
                            )}

                        </div>
                    )}
                    {autogiroLoading ? (
                        <FetchingAnimation
                            width={155}
                            height={40}
                            borderRadius={9999}
                        />
                    ) : (
                        showAutogiroButton && (
                            <RightsWrapper
                                disableButtonText='Autogiro'
                                disableButtonClasses={classNames('body-text')}
                                toolTipPosition='top-start'
                            >
                                <Button
                                    className="body-text button-edit"
                                    type="button"
                                    onClick={openAutogiroDialog}
                                >
                                    {autogiroApproved ? 'Hantera autogiro' : 'Anslut autogiro'}
                                </Button>
                            </RightsWrapper>
                        )
                    )}
                </div>
            </div>

            {/* Dialog for managing autogiro */}
            <dialog
                ref={dialogRefAutogiro}
                className="handle-autogiro-dialog"
                onCancel={handleCancelAutogiro}
            >
                <h2 className="autogiro__title body-text">
                    <span className="svg-icon-wrapper blue round">
                        <ConnectPlugIcon width={28} height={28} />
                    </span>
                    {autogiroApproved ? 'Hantera autogiro' : 'Anslut autogiro'}
                </h2>
                <div className="handle-autogiro-dialog-content">
                    {!autogiroApproved ? (
                        <>
                            <p>
                                <strong>Vill du byta cleringnr och kontonummer för ditt autogiro?</strong><br />
                                Fyll i det nya clearing- och kontonumret nedan. Din ansökan kommer att behandlas och du kommer att få en bekräftelse när ändringen är godkänd.
                            </p>
                            <p className="margin-top">
                                Du kan även ta bort autogiro-kopplingen genom att klicka på knappen nedan
                            </p>
                        </>
                    ) : (
                        <>
                            <p>
                                Anslut till autogiro för att betala dina fakturor automatiskt på förfallodagen. Fyll i clearing- och kontonummer nedan.
                            </p>
                            <p>
                                När du ansluter autogiro kommer dina fakturor att betalas automatiskt varje månad på förfallodagen.
                            </p>
                        </>
                    )}

                    <div className="autogiro-form-wrapper">
                        <form id="autogiro-form">
                            <div className="clearing-number-input input-wrapper">
                                <span>
                                    Clearing<span className="color-red">*</span>
                                    <Tooltip position='top' text="Clearing är det nummer som identifierar vilken bank du har, det är vanligtvis 4 siffror. Om ditt clearing nummer innehåller 5 siffror tar du bort den sista siffran.">
                                        <InfoIconOutlined width={20} height={20} />
                                    </Tooltip>
                                </span>
                                <input
                                    type="text"
                                    name="clearingNumber"
                                    placeholder="1234"
                                    value={clearingNumber}
                                    onChange={handleClearingNumberChange}
                                    onBlur={handleClearingNumberBlur}
                                    maxLength={4}
                                />
                                {clearingNumberError && clearingNumber.length > 0 && (
                                    <span className="input-error">{clearingNumberError}</span>
                                )}
                            </div>
                            <div className="account-number-input input-wrapper">
                                <span>
                                    Kontonummer<span className="color-red">*</span>
                                    <Tooltip position='top' text="Kontonummer är det nummer som identifierar ditt bankkonto.">
                                        <InfoIconOutlined width={20} height={20} />
                                    </Tooltip>
                                </span>
                                <input
                                    type="text"
                                    name="bankNumber"
                                    placeholder=""
                                    value={bankNumber}
                                    onChange={handleBankNumberChange}
                                    onBlur={handleBankNumberBlur}
                                    maxLength={15}
                                />
                                {bankNumberError && bankNumber.length > 0 && (
                                    <span className="input-error">{bankNumberError}</span>
                                )}
                            </div>
                        </form>
                    </div>
                </div>
                <div className="handle-autogiro__actions">
                    <Button
                        type="button"
                        onClick={handleSubmitAutogiro}
                        className="button body-text"
                        disabled={mutationLoading || !isFormValid()}
                    >
                        {mutationLoading ? 'Skickar...' : showAutogiroButton && autogiroApproved ? 'Uppdatera' : 'Ansök'}
                    </Button>
                    <Button type="button" onClick={handleCancelAutogiro} className="button-cancel body-text">
                        Avbryt
                    </Button>

                    {autogiroApproved && (
                        <Button
                            type="button"
                            onClick={handleDeleteAutogiro}
                            className="button button-delete body-text"
                            disabled={mutationLoading}
                        >
                            {mutationLoading ? 'Tar bort...' : 'Ta bort autogiro'}
                        </Button>
                    )}
                </div>
            </dialog>
        </div>
    );
};

export default AutogiroSettings;
